import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import "./LicenseType.css";
import {
  hasUserAccess,
  getUserRole,
} from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import PageLoader from "../../../shared/PageLoader";
import { useHistory } from "raviger";
import Table, {  
  CellTmpl,
  DEFAULT_PAYLOAD,
  TitleTmpl,
  reactFormatter,
} from "../common/Table";
import {
  MONTHS_ENUM,
} from "../../../common/Constants";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {preventDefault} from "../../../../utils/common";
import LicenseTypeDetail from "./LicenseTypeDetail";
import { useGetLicenseTypes } from "../../../../backend/license";
import LicenseFilters from "../common/LicenseFilters";
import { useSidenav } from "../../../../contexts/SidenavContext";
import AddNewLicenseTypePanel from "./AddNewLicenseTypePanel";
import SearchBox from "../common/SearchBox";

function getAdvanceSearch(state: any) {
  const filters = [];
  if ((state || {}).fromDate) {
    filters.push({
      columnName: "fromDate",
      value: dayjs(state.fromDate).startOf("D").toISOString(),
    });
    filters.push({
      columnName: "toDate",
      value: dayjs(state.toDate).endOf("D").toISOString(),
    });
  }

  if ((state || {}).auditType) {
    filters.push({ columnName: "auditType", value: state.auditType });
  }
  return filters.length > 0 ? filters : undefined;
}

function LicenseType() {
  const { state }: any = useHistory();
  const { toggleSidenav } = useSidenav();
  const [checkedStatuses, setCheckedStatuses] = useState<any>(
    (state || {}).status ? { [state.status]: true } : {}
  );
  const [data, setData] = useState<any>();
  const [params, setParams] = useState<any>();
  const [locationFilters, setLocationFilter] = useState<any>();
  const lfRef: any = useRef();
  lfRef.current = locationFilters;
  const [advaceSearchFilters, setAdvanceSearchFilters] = useState(
    getAdvanceSearch(state)
  );
  const afRef: any = useRef();
  afRef.current = advaceSearchFilters;
  const [statusFilters, setStatusFilters] = useState<any>();
  const sfRef = useRef();
  sfRef.current = statusFilters;
  const [payload, setPayload] = useState<any>();
  const payloadRef = useRef();
  payloadRef.current = payload;
  const lastPayloadRef = useRef<string>();
  const { licenseTypes, total, isFetching, refetch, companyIds } = useGetLicenseTypes(
   payload,
   Boolean(payload)
  );

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [previousFilters, setPreviousFilters] = useState<any>({});
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isLicenseDetailOpen, setLicenseDetailOpen] = useState(false);
  const [selectedLicenseType, setSelectedLicenseType] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchFilters, setSearchFilters] = useState<any>({});
  const searchRef = useRef();
  searchRef.current = searchFilters;

  const onLocationChange = useCallback(
    (filters: any) => {
      // If empty filters object, set to undefined to trigger proper reset
      setLocationFilter(Object.keys(filters).length === 0 ? undefined : filters);
      setPreviousFilters(filters);
    },
    [params]
  );

 

  useEffect(() => {
    checkFirstTimePublish();
  }, [licenseTypes]);

  function checkFirstTimePublish() {
    const { month_eq, year_eq } = getAdvanceSearchPayload();
    if (month_eq && year_eq) {
      const _filter = {
        ...previousFilters,
      };
      const date = new Date();
      date.setDate(1);
      date.setFullYear(parseInt(`${year_eq}`));
      date.setMonth(MONTHS_ENUM.indexOf(month_eq));
      _filter["monthYear"] = date;
      setPreviousFilters(_filter);
    }
  }

  function ActiveStatusTmpl({ cell }: any) {
    const isActive = cell.getValue();

    return (
      <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
        {isActive ? 'Yes' : 'No'}
      </span>
    );
  }

  function StateCityTmpl({ cell }: any) {
    const value = cell.getValue();
    const row = cell.getData();

    // Check if both state and city are empty
    const hasState = row.state && row.state.name;
    const hasCity = row.city && row.city.name;

    if (!hasState && !hasCity) {
      return <span>Global</span>;
    }

    return <span>{value || '-'}</span>;
  }

  function ActionsTmpl({ cell }: any) {
    const row = cell.getData();
    return (
      <div className="cursor-pointer">
        <div onClick={() => {
          toggleSidenav(false);
          setSelectedLicenseType(row);
          setLicenseDetailOpen(true);
        }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                <path d="M0.666626 8.65627C0.666626 8.65627 3.33329 3.32294 7.99996 3.32294C12.6666 3.32294 15.3333 8.65627 15.3333 8.65627C15.3333 8.65627 12.6666 13.9896 7.99996 13.9896C3.33329 13.9896 0.666626 8.65627 0.666626 8.65627Z" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.99996 10.6563C9.10453 10.6563 9.99996 9.76084 9.99996 8.65627C9.99996 7.5517 9.10453 6.65627 7.99996 6.65627C6.89539 6.65627 5.99996 7.5517 5.99996 8.65627C5.99996 9.76084 6.89539 10.6563 7.99996 10.6563Z" stroke="#2965AD" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </div>
      </div>
    );
  }

  function DateTmpl({ cell }: any) {
    const value = cell.getValue();
    return value ? dayjs(value).format("DD-MM-YYYY") : "-";
  }

  function NameTmpl({ cell }: any) {
    const row = cell.getData();
    const value = row.name || "";
    return (
      <OverlayTrigger
        overlay={<Tooltip>{value}</Tooltip>}
        rootClose={true}
        placement="bottom"
      >
        <div className="d-flex align-items-center h-100 w-auto">
          <span className="ellipse two-lines">{value}</span>
        </div>
      </OverlayTrigger>
    );
  }

  const columns = [
    {
      title: "IDs",
      field: "id",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerClick: (e: any, col: any) => {
        preventDefault(e);
      },
    },
    {
      title: "License Type",
      field: "name",
      formatter: reactFormatter(<NameTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      headerClick: (e: any, col: any) => {
        preventDefault(e);
      },
    },
    {
      title: "Company",
      field: "company.name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Associate Company",
      field: "associate_company.name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "State",
      field: "state.name",
      formatter: reactFormatter(<StateCityTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "City",
      field: "city.name",
      formatter: reactFormatter(<StateCityTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Created on",
      field: "created_at",
      formatter: reactFormatter(<DateTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Active",
      field: "is_active",
      formatter: reactFormatter(<ActiveStatusTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Actions",
      field: "actions",
      formatter: reactFormatter(<ActionsTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
  ];

  const [tableConfig] = useState({
    paginationMode: "remote",
    ajaxRequestFunc,
    columns,
    rowHeight: "auto",
    selectable: getUserRole() === "License Manager" ? true : false,
    paginate: true,
  });

  function formatApiResponse(params: any, list: any, totalRecords: number) {
    const { page, size } = params || {};
    const tdata = {
      data: list,
      total: totalRecords,
      last_page: Math.ceil(totalRecords / (size || 10)) || 1,
      page: page || 1,
    };
    return tdata;
  }

  function ajaxRequestFunc(url: any, config: any, params: any) {
    // Wait for auto-select to complete
    if (lfRef.current === undefined) {
      return Promise.resolve(formatApiResponse(params, [], 0));
    }

    return Promise.resolve(
      formatApiResponse(params, licenseTypes || [], total || 0)
    );
  }              

  function handlePageNav(_pagination: any) {
    const _params = {
      per: _pagination.per || 10,
      page: _pagination.page || 1,
      include: ["company", "associate_company", "state", "city"],
      includes: ["company", "associate_company", "state", "city"],
      sorts: (params && params.sorts) || ["created_at desc"],
      filters: {
        ...(lfRef.current || {}),
        ...(sfRef.current || {}),
        ...(searchRef.current || {}),
      }
    };
    const finalPayload = {
      ...DEFAULT_PAYLOAD,
      ..._params,
    };
    setParams({ ..._params });
    setPayload(finalPayload);
  }

  function getAdvanceSearchPayload() {
    return { ...(afRef.current || {}) };
  }

  useEffect(() => {
    // Only fire if locationFilters has actual filter values (not undefined or empty)
    const hasLocationFilters = locationFilters && Object.keys(locationFilters).length > 0;
    const hasStatusFilters = statusFilters && Object.keys(statusFilters).length > 0;
    const hasSearchFilters = searchFilters && Object.keys(searchFilters).length > 0;

    if (hasLocationFilters || hasStatusFilters || hasSearchFilters) {
      const timeoutId = setTimeout(() => {
        const _params = {
          per: (params && params.per) || 10,
          page: 1,
          include: ["company", "associate_company", "state", "city"],
          includes: ["company", "associate_company", "state", "city"],
        };
        const finalPayload = {
          ...DEFAULT_PAYLOAD,
          ..._params,
          sorts: (payload && payload.sorts) || ["created_at desc"],
          filters: {
            ...(lfRef.current || {}),
            ...(afRef.current || {}),
            ...(sfRef.current || {}),
            ...(searchRef.current || {}),
          },
        };

        const payloadString = JSON.stringify(finalPayload);
        if (lastPayloadRef.current !== payloadString) {
          lastPayloadRef.current = payloadString;
          setParams({ ..._params });
          setPayload(finalPayload);
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [locationFilters, statusFilters, searchFilters]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        setSearchFilters({ name_cont: searchQuery.trim() });
      } else {
        setSearchFilters({});
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    if (checkedStatuses) {
      const keys = Object.keys(checkedStatuses);
      const result = keys.filter((key) => !!checkedStatuses[key]);
      const _result: any = {};
      if (result.length > 0) {
        _result["status_in"] = result;
      }
      setStatusFilters(_result);
    }
  }, [checkedStatuses]);

  useEffect(() => {
    if (!isFetching && payload) {
      setSelectedRows([]);
      setData(formatApiResponse(params, licenseTypes || [], total || 0));
    }
  }, [isFetching]);

  return (
    <>
      <div className="licensetype-wrapper">
        <div className="d-flex flex-column license-dashboard">
          <div className="d-flex p-3 align-items-center pageHeading">
            <div>
              <h4 className="mb-0">License types</h4>
              <p>
                Define custom license types for licenses applications. 
              </p>
            </div>
            <div className="d-flex align-items-end h-100 gap-3 w-50 justify-content-end">
              <SearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by license type name"
              />
              {hasUserAccess(USER_PRIVILEGES.ADD_LICENSE_TYPE) && (
                <button className="button-primary" onClick={() => {
                  toggleSidenav(false);
                  setIsSidePanelOpen(true);
                }}>Create License Type</button>
              )}
            </div>
          </div>

          <form className="p-0 mx-3">
            <div className="card border-0 py-4 px-2">
              <div className="d-flex flex-row filters">
                <LicenseFilters onChange={onLocationChange} showStateCityInsteadOfLocation/>
              </div>
            </div>
          </form>
          <div className="position-relative p-3 pb-0 license-table">
            <Table
              data={data}
              options={tableConfig}
              isLoading={isFetching}
              onSelectionChange={setSelectedRows}
              onPageNav={handlePageNav}
            />
          </div>
        </div>
        <AddNewLicenseTypePanel
          isOpen={isSidePanelOpen}
          closePanel={() => setIsSidePanelOpen(false)}
          onSuccess={() => {                                                                                                
            refetch();                                                                                                      
            setIsSidePanelOpen(false);  
          }}
        />
        <LicenseTypeDetail
          isOpen={isLicenseDetailOpen}
          closePanel={() => {
            setLicenseDetailOpen(false);
            setSelectedLicenseType(null);
          }}
          license={selectedLicenseType}
          refetch={refetch}
        />
      </div>
      {isFetching && <PageLoader />}
    </>
  );
}

export default LicenseType;
